import type { DashboardFetcher, DashboardPeriod, SchemaField } from '../types';
import type { EndpointMeta } from './dashboardProxy';
import { fetchWidgetDataProxy } from './dashboardProxy';

/**
 * La entrada de caché de los datos de un widget: su clave y cómo se construye su contenido.
 *
 * <b>Por qué existe este módulo.</b> Dos consumidores piden los datos de un mismo widget: el
 * propio widget (`useWidgetFetch`) y la fuente secundaria de un KPI (`useSecondarySource`, el
 * sparkline). Comparten clave <b>a propósito</b> —si el gráfico grande ya está en pantalla, el
 * sparkline no cuesta ninguna petición— y eso los obliga a guardar exactamente lo mismo.
 *
 * Ya se rompió una vez por la FORMA (uno guardaba el array pelado y el otro el envelope) y se
 * corrigió. Pero seguía roto por el CONTENIDO, que es más difícil de ver: `useWidgetFetch`
 * aplicaba `field_mapping.rename` dentro de su `queryFn` y la fuente secundaria no, así que si el
 * sparkline ganaba la carrera el gráfico grande leía la entrada <b>sin renombrar</b> y sus
 * `valueKeys` no existían en las filas. El `queryFn` del que pierde la carrera no se ejecuta
 * nunca, así que su transformación tampoco.
 *
 * Teniendo la clave y el contenido en un solo sitio, no pueden volver a divergir.
 */

/** Parámetros extra del proxy. Primitivos, como los admite su DTO. */
export type WidgetUserParams = Record<string, string | number | boolean>;

export interface CachedWidgetEnvelope<T = unknown> {
  data: T;
  schema: { fields: SchemaField[] } | null;
  meta: EndpointMeta | null;
}

/**
 * La clave de caché de los datos de un widget.
 *
 * <b>`userParams` forma parte de la identidad de la petición</b>, porque cambia la respuesta:
 * un `{ points: 7 }` devuelve siete puntos. Sin ellos en la clave, el sparkline y el gráfico
 * grande compartían entrada pidiendo cosas distintas y ganaba el que resolviera primero.
 *
 * Cuando no hay `userParams` la clave queda IDÉNTICA a la de siempre — que es justo el objetivo:
 * compartir la entrada cuando la petición es la misma, y separarla cuando no lo es.
 */
export function widgetDataQueryKey(
  widgetId: string | undefined,
  period: DashboardPeriod,
  userParams?: WidgetUserParams,
): unknown[] {
  const base = ['widget-data', widgetId, period];
  return userParams && Object.keys(userParams).length > 0 ? [...base, userParams] : base;
}

/** Accede a un valor anidado vía dot-notation (`name.common` → `obj.name.common`). */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) =>
    acc != null && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined, obj);
}

/** Aplica `field_mapping.rename`: expone cada campo del endpoint con el nombre que el widget espera. */
export function applyFieldMapping(data: unknown, mapping: Record<string, string>): unknown {
  const entries = Object.entries(mapping);
  if (entries.length === 0) return data;

  if (process.env.NODE_ENV === 'development') {
    const sample = Array.isArray(data) ? data[0] : data;
    if (sample && typeof sample === 'object') {
      for (const [, apiField] of entries) {
        const topLevelField = apiField.split('.')[0];
        if (!(topLevelField in (sample as object))) {
          // eslint-disable-next-line no-console
          console.warn(
            `[KPI Dashboard] field_mapping.rename: el campo "${apiField}" no existe en la respuesta del endpoint. ` +
            `Verificar que el nombre coincida exactamente con el campo devuelto por la API.`
          );
        }
      }
    }
  }

  const mapObject = (obj: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = { ...obj };
    for (const [widgetField, apiField] of entries) {
      const val = apiField.includes('.') ? getNestedValue(obj, apiField) : obj[apiField];
      if (val !== undefined) {
        result[widgetField] = val;
      }
    }
    return result;
  };

  if (Array.isArray(data)) {
    return data.map(item =>
      typeof item === 'object' && item !== null ? mapObject(item as Record<string, unknown>) : item
    );
  }
  if (typeof data === 'object' && data !== null) {
    return mapObject(data as Record<string, unknown>);
  }
  return data;
}

/**
 * El contenido EXACTO que se guarda bajo {@link widgetDataQueryKey}.
 *
 * Todo consumidor que comparta la clave tiene que llamar a esta función: es lo que garantiza que
 * la entrada de caché sea la misma sin importar quién gane la carrera.
 */
export async function fetchWidgetEnvelope<T>(
  params: { widgetId: string; period: DashboardPeriod; userParams?: WidgetUserParams },
  fieldMapping: Record<string, string> | undefined,
  fetcher: DashboardFetcher,
): Promise<CachedWidgetEnvelope<T>> {
  const { data: raw, schema, endpointMeta } = await fetchWidgetDataProxy(
    { widgetId: params.widgetId, period: params.period, userParams: params.userParams },
    fetcher,
  );
  const mapped = fieldMapping && Object.keys(fieldMapping).length > 0
    ? applyFieldMapping(raw, fieldMapping)
    : raw;
  return { data: mapped as T, schema, meta: endpointMeta };
}
